import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Stack } from '@chakra-ui/react';

import { useTrackLoadingState } from 'common/sentry/webAppOpeningTransaction';
import Heading from 'components/_core/Heading';
import SunshineCard from 'components/_core/SunshineCard';
import SquircleIcon from 'components/_core/SunshineSquircleIcon';
import routes from 'features/Payments/routes';
import * as ContentLayout from 'layouts/ContentLayout';

import mandatesRoutes from '../DirectDebitMandates/routes';
import transfersRoutes from '../Transfers/routes';
import { usePaymentsHomeQuery } from './hooks/usePaymentsHomeQuery';
import locales from './locales';

function textWithCount(text: string, count?: number) {
  return count !== undefined ? `${text} (${count})` : text;
}

const PaymentsHome: FC = () => {
  const {
    beneficiariesCount,
    loading,
    mandatesCount,
    scheduledTransfersCount,
  } = usePaymentsHomeQuery();

  /**
   * Track this page as being fully loaded.
   * @warning please ensure all loaders that impact the rendering of the meaningful content of this page are provided to this hook.
   */
  useTrackLoadingState({
    destination: 'payments-home',
    isLoaded: !loading,
  });

  const linkToBeneficiaries = textWithCount(
    locales.links.seeBeneficiaries,
    beneficiariesCount,
  );
  const linkToMandates = textWithCount(
    locales.links.seeMandates,
    mandatesCount,
  );

  const linkToScheduledTransfers = textWithCount(
    locales.links.seeScheduledTransfers,
    scheduledTransfersCount,
  );

  return (
    <ContentLayout.Root>
      <ContentLayout.Body>
        <Heading marginBottom="space-40" size="lg">
          {locales.title}
        </Heading>

        <Stack spacing="space-32">
          <SunshineCard.Group>
            <SunshineCard
              alignItems="center"
              as={Link}
              title={locales.links.createNewTransfer}
              to={routes.transfers}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="transaction-out" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              as={Link}
              title={locales.links.createNewMandate}
              to="/payments/mandates/add"
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="add-file" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              as={Link}
              title={locales.links.createNewBeneficiary}
              to={{
                pathname: transfersRoutes.newBeneficiary,
              }}
            >
              <SunshineCard.Slot name="aside">
                <SquircleIcon name="user-plus" />
              </SunshineCard.Slot>

              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          </SunshineCard.Group>

          <SunshineCard.Group border="none" divider={<></>}>
            <SunshineCard
              alignItems="center"
              as={Link}
              title={linkToScheduledTransfers}
              to={routes.scheduledTransfers}
            >
              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              as={Link}
              title={linkToMandates}
              to={mandatesRoutes.root}
            >
              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>

            <SunshineCard
              alignItems="center"
              as={Link}
              title={linkToBeneficiaries}
              to={transfersRoutes.beneficiaries}
            >
              <SunshineCard.Slot name="action">
                <SunshineCard.SunshineIcon name="chevron-right" />
              </SunshineCard.Slot>
            </SunshineCard>
          </SunshineCard.Group>
        </Stack>
      </ContentLayout.Body>
    </ContentLayout.Root>
  );
};

export default PaymentsHome;
